import { useEffect, useMemo } from 'react';
import type { TaskBodyProps } from '../index';
import { DesignRejectFooter } from '../DesignReviewFooter';
import { DesignDxTaskLayout, PastSubmissions } from './DesignDxTaskLayout';
import { useFinalReviewHandBack } from './useFinalReviewHandBack';
import { useBeforeCloseFlush } from './useBeforeCloseFlush';
import { OPEN_DX_DRAFT, useDxDraftRequiredHint } from './useDxDraftRequiredHint';
import { designLoopActions } from './designLoop';

// Only Design pre-approved has this edge; its route needs new instructions, so the body owns it.
const REJECT_ACTION = 'reject-design-review';
// From Design pre-approved, Plan received skips review and needs a Dx draft; elsewhere it doesn't.
const DESIGN_PRE_APPROVED_STATUS_ID = 'Init - 4.1. Design pre-approved';
const PLAN_RECEIVED_ACTION = 'plan-received';

export function WaitForDesignTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete,
    onRegisterBeforeClose
}: TaskBodyProps) {
    const handBack = useFinalReviewHandBack(c.id);
    const { registerBeforeClose, flush } = useBeforeCloseFlush(onRegisterBeforeClose);
    const { submitTypes } = designLoopActions(statusMeta.macro);
    const submissions = useMemo(
        () => <PastSubmissions patientId={c.id} submitTypes={submitTypes} handBack={handBack} />,
        [c.id, submitTypes, handBack]
    );
    const rejectAction = statusMeta.outgoingActions.find((a) => a.action === REJECT_ACTION);
    const hasReject = rejectAction !== undefined;
    const dxDraftHint = useDxDraftRequiredHint(c.id, OPEN_DX_DRAFT);
    const gatesPlanReceived = statusMeta.id === DESIGN_PRE_APPROVED_STATUS_ID;
    const planReceivedHint = gatesPlanReceived ? dxDraftHint : null;

    useEffect(() => {
        if (!hasReject && !gatesPlanReceived) return;
        onPayloadChange?.({
            payload: null,
            valid: true,
            hiddenActions: hasReject ? [REJECT_ACTION] : undefined,
            disabledActions: planReceivedHint
                ? { [PLAN_RECEIVED_ACTION]: planReceivedHint }
                : undefined
        });
    }, [hasReject, gatesPlanReceived, planReceivedHint, onPayloadChange]);

    const layout = (
        <DesignDxTaskLayout
            workCase={c}
            statusMeta={statusMeta}
            submissions={submissions}
            onRegisterBeforeClose={registerBeforeClose}
            handBack={handBack}
        />
    );
    if (!rejectAction) return layout;
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {layout}
            <DesignRejectFooter
                caseId={c.id}
                rejectAction={rejectAction}
                onTaskComplete={onTaskComplete}
                beforeAction={flush}
            />
        </div>
    );
}
